import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@d-environments/environment';
import { BaseSearchRequest } from '@d-models';
import { IUser, IUserGroup } from '@d-models/other/user.model';
import { HttpService } from '@d-services/other/http.service';
import dayjs from 'dayjs';
import { CoreHttpService } from './core-http.service';
import { ModalService } from '@d-services/other/modal.service';
import { of, tap } from 'rxjs';
import { CoreUserService } from './user.service';
import { USER_LEVEL } from '@d-constants/user.constant';

@Injectable()
export class EmployeeService {
  private apiUrl: string = environment.API_URL;

  constructor(
    private http: HttpClient,
    private httpService: HttpService,
    private coreHttp: CoreHttpService,
    private modalService: ModalService,
    private userCoreService: CoreUserService,
  ) {}

  getAll() {
    return this.coreHttp.get<any[]>('Employee/GetAll', {
      useTenantCompanyId: true,
    });
  }
  getAllData() {
    return this.coreHttp.get<any[]>('Employee/getAllData', {
      useTenantCompanyId: true,
    });
  }
  getAllDeleted() {
    return this.coreHttp.get<any[]>('Employee/GetAllDeleted', {
      useTenantCompanyId: true,
    });
  }

  searchEmployee(
    request: BaseSearchRequest,
    { headers = {} } = {},
    doUseTenantCompanyId?: boolean,
  ) {
    const body = this.httpService.buildSearchRequest(request);
    const {
      pageIndex,
      pageSize,
      sortBy,
      searchGlobal,
      advancedFilter,
      statuses,
    } = body;
    const filter: any = advancedFilter || {};
    if (searchGlobal.filterBy && searchGlobal.searchBy) {
      filter[searchGlobal.filterBy] = searchGlobal.searchBy;
    }
    const sort =
      sortBy?.map((sort) => ({
        field:
          sort.fieldName.charAt(0)?.toLowerCase() + sort.fieldName.slice(1),
        asc: sort.ascending,
      })) || [];
    filter.status = advancedFilter?.['status'] ?? statuses?.[0];
    return this.coreHttp.post<IUser[]>(
      'Employee/GetAllByPaging',
      {
        pageIndex: pageIndex - 1,
        pageSize,
        sort,
        filter: Object.entries(filter)
          .filter(([_, v]) => v !== null && v !== undefined)
          .map(([key, value]) => ({
            field: key,
            valueString: value,
          })),
      },
      {
        headers,
        useTenantCompanyId: doUseTenantCompanyId,
      },
    );
  }

  searchEmployeeDeleted(postData: any) {
    return this.coreHttp.post<any[]>('Employee/GetAllDeleted', postData, {
      useTenantCompanyId: true,
    });
  }

  getDetail(id: string, doUseTenantCompanyId: boolean = true) {
    return this.coreHttp.get<IUser>(`Employee/GetById?id=${id}`, {
      useTenantCompanyId: doUseTenantCompanyId,
    });
  }

  getUserGroup(employeeId: string) {
    return this.coreHttp.get(`Employee/GetUserGroup?employeeId=${employeeId}`);
  }

  getDetailUserGroup(id: string) {
    return this.coreHttp.get<IUserGroup>(`Groups/GetById?id=${id}`);
  }

  searchEmployeeGroup(request: BaseSearchRequest) {
    // category: string
    const body = this.httpService.buildSearchRequest(request);
    const {
      pageIndex,
      pageSize,
      sortBy,
      searchGlobal,
      advancedFilter,
      statuses,
    } = body;

    const filter: any = advancedFilter || {};
    if (searchGlobal.filterBy && searchGlobal.searchBy) {
      filter[searchGlobal.filterBy] = searchGlobal.searchBy;
    }

    const sort =
      sortBy?.map((sort) => ({
        field:
          sort.fieldName.charAt(0)?.toLowerCase() + sort.fieldName.slice(1),
        asc: sort.ascending,
      })) || [];
    filter.status = advancedFilter?.['status'] ?? statuses?.[0];
    // filter.category = category;

    const payload = {
      pageIndex: pageIndex - 1,
      pageSize,
      sort,
      filter: Object.entries(filter)
        .filter(([_, v]) => v !== null && v !== undefined)
        .map(([key, value]) => ({
          field: key,
          valueString: value,
        })),
    };
    return this.coreHttp.post<IUserGroup[]>('Groups/GetAllByPaging', payload);
  }

  addUserGroup(postData: any) {
    return this.coreHttp.post('Groups/Create', postData);
  }

  updateUserGroup(putData: any) {
    return this.coreHttp.put(`Groups/Update?id=${putData.id}`, putData);
  }

  populateData(data: any) {
    return {
      dateBirth: dayjs().format('YYYY-MM-DD'),
      gender: 'Male',
      identityNo: 'xxxxxx',
      nationalityId: data.countryId,
      countryPrefixId: data.countryId,
      allowLogin: true,
      isUserEdited: true,
      GroupIds: [],
      merchantIds: data.merchantIds,
      ...data,
    };
  }

  addEmployee(
    data: any,
    { headers = {} } = {},
    doUseTenantCompanyId: boolean = true,
  ) {
    return this.coreHttp.post('Employee/Create', this.populateData(data), {
      headers,
      useTenantCompanyId: doUseTenantCompanyId,
    });
  }

  updateEmployee(data: any, doUseTenantCompanyId: boolean = true) {
    return this.coreHttp.put(
      `Employee/Update?id=${data.id}`,
      this.populateData(data),
      {
        useTenantCompanyId: doUseTenantCompanyId,
      },
    );
  }

  copyEmployee(postData: any) {
    return this.coreHttp.post(`Employee/Clone?id=${postData.id}`, postData, {
      useTenantCompanyId: true,
    });
  }

  updateProfile(id: string, postData: any) {
    return this.coreHttp.post(`Employee/UpdateProfile?id=${id}`, postData, {
      useTenantCompanyId: true,
    });
  }

  deleteEmployee(ids: string[], doUseTenantCompanyId: boolean = true) {
    return this.coreHttp.post('Employee/DeleteMulti', ids, {
      useTenantCompanyId: doUseTenantCompanyId,
    });
  }

  deleteGroups(ids: string[]) {
    return this.coreHttp.post('Groups/DeleteMulti', ids);
  }

  restoreEmployee(ids: string[]) {
    return this.coreHttp.post('Employee/RestoreMulti', ids);
  }

  getMyProfile(id: string) {
    return this.coreHttp.get(`Employee/GetMyProfile?id=${id}`, {
      useTenantCompanyId: true,
    });
  }

  nextUserId(headers = {}, hideLoading = false) {
    if (!hideLoading) this.modalService.showLoading();
    return this.http
      .post<any>(
        `${this.apiUrl}/master-data/sequence-no/generate`,
        {
          SystemId:
            this.userCoreService.getUserLevel().level ===
            USER_LEVEL.TENANT_LEVEL
              ? 'TenantUserId'
              : 'UserID',
          saveSequenceNo: true,
        },
        {
          headers: this.coreHttp.buildHeaders(headers, {
            useTenantCompanyId: true,
            useUserInfoCompanyId: false,
          }),
        },
      )
      .pipe(tap<any>(() => !hideLoading && this.modalService.hideLoading()));
  }
}
